import { Sparkles, Bookmark, Trash2, Share2, Zap, FileText } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ShareResourceModal } from '@/components/collaboration/ShareResourceModal';
import { shareInviteService } from '@/services/shareInvites';
import { useAppStore } from '@/stores/appStore';
import { useTabMonitorSessionOptional } from '@/context/TabMonitorSessionContext';
import { Button } from '@components/ui/Button';
import { Input, Textarea } from '@components/ui/Input';
import { Modal, ConfirmDialog } from '@components/ui/Modal';
import { formatRelativeTime, cn } from '@/utils';
import { AppCardPreview } from '@/components/apps/AppCardPreview';
import { getAppCardDescription, resolveAppDocument } from '@/components/apps/appDocument';
import type { GeneratedApp, AppType } from '@/types';

const statusColors = {
  pending: 'bg-warning-500/20 text-warning-400 border border-warning-500/30',
  processing: 'bg-primary-500/20 text-primary-400 border border-primary-500/30',
  completed: 'bg-success-500/20 text-success-400 border border-success-500/30',
  failed: 'bg-error-500/20 text-error-400 border border-error-500/30',
};

/**
 * IUI Apps Page
 * 
 * Clean app listing with generation capability.
 * Follows IUI design principles.
 */
export function AppsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const apps = useAppStore((state) => state.apps);
  const isLoading = useAppStore((state) => state.isLoading);
  const error = useAppStore((state) => state.error);
  const fetchApps = useAppStore((state) => state.fetchApps);
  const createApp = useAppStore((state) => state.createApp);
  const toggleBookmark = useAppStore((state) => state.toggleBookmark);
  const deleteApp = useAppStore((state) => state.deleteApp);
  const sessionCtx = useTabMonitorSessionOptional();
  const workspaceSessionId = sessionCtx?.workspaceSessionId;

  useEffect(() => {
    void fetchApps(workspaceSessionId ?? undefined);
  }, [fetchApps, workspaceSessionId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    const acceptId = searchParams.get('accept-share');
    if (!acceptId) return;
    void (async () => {
      try {
        const inv = await shareInviteService.accept(acceptId);
        toast.success(`You now have access to "${inv.resource_name}"`);
        void fetchApps(workspaceSessionId ?? undefined);
      } catch (err) {
        toast.error((err as Error)?.message ?? 'Failed to accept invitation');
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('accept-share');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, fetchApps, workspaceSessionId]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<AppType | 'all' | 'shared'>('all');
  const [generationModeFilter, setGenerationModeFilter] = useState<'all' | 'static' | 'dynamic'>('all');
  const [appToDelete, setAppToDelete] = useState<GeneratedApp | null>(null);
  const [shareTarget, setShareTarget] = useState<GeneratedApp | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create form state
  const [appName, setAppName] = useState('');
  const [description, setDescription] = useState('');
  const [appType, setAppType] = useState<AppType>('productivity');

  const appTypes: AppType[] = ['productivity', 'social', 'e-commerce', 'blog', 'portfolio', 'dashboard', 'custom'];

  /** Dynamic apps have a live preview URL or were built with the multi-step pipeline. */
  const isDynamicApp = (app: GeneratedApp): boolean =>
    app.app_type === 'custom' || 
    app.app_type === 'dynamic' ||
    Boolean(app.preview_url) ||
    Boolean(app.metadata?.cdn_url) ||
    Boolean(app.metadata?.pipeline) ||
    Boolean((app.metadata as Record<string, unknown>)?.pipeline_used) ||
    (app.generated_files && Object.keys(app.generated_files).length > 0);

  const filteredApps = apps.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType =
      filterType === 'all' ||
      (filterType === 'shared' ? app.is_shared === true : app.app_type === filterType);
    const matchesMode =
      generationModeFilter === 'all' ||
      (generationModeFilter === 'dynamic' ? isDynamicApp(app) : !isDynamicApp(app));
    return matchesSearch && matchesType && matchesMode;
  });

  const dynamicCount = apps.filter(isDynamicApp).length;
  const staticCount = apps.length - dynamicCount;

  const handleCreateApp = async () => {
    try {
      await createApp({
        name: appName || undefined,
        description: description || undefined,
        app_type: appType,
      });
      toast.success('App generation started!');
      setShowCreateModal(false);
      setAppName('');
      setDescription('');
    } catch (error) {
      toast.error('Failed to create app');
    }
  };

  const handleToggleBookmark = async (appId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleBookmark(appId);
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleDeleteClick = (app: GeneratedApp, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAppToDelete(app);
  };

  const handleConfirmDelete = async () => {
    if (!appToDelete) return;
    setIsDeleting(true);
    try {
      await deleteApp(appToDelete.id);
      toast.success('App deleted');
      setAppToDelete(null);
    } catch {
      toast.error('Failed to delete app');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="h-full p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-khiimori-text-primary">Generated Apps</h1>
          <p className="text-khiimori-text-muted mt-1">
            {apps.filter((a) => a.status === 'completed').length} completed apps
          </p>
        </div>
        <Button leftIcon={<Sparkles className="w-5 h-5" />} onClick={() => setShowCreateModal(true)}>
          Generate App
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <Input
            placeholder="Search apps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
          {/* Generation mode filter */}
          <div className="flex gap-2 items-center">
            <span className="text-xs text-khiimori-text-muted font-medium uppercase tracking-wider">Mode:</span>
            <button
              onClick={() => setGenerationModeFilter('all')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150 flex items-center gap-1.5',
                generationModeFilter === 'all'
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
              )}
            >
              All
            </button>
            <button
              onClick={() => setGenerationModeFilter('dynamic')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150 flex items-center gap-1.5',
                generationModeFilter === 'dynamic'
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
              )}
            >
              <Zap className="w-3.5 h-3.5" />
              Dynamic
              <span className="text-xs opacity-70">({dynamicCount})</span>
            </button>
            <button
              onClick={() => setGenerationModeFilter('static')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150 flex items-center gap-1.5',
                generationModeFilter === 'static'
                  ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
              )}
            >
              <FileText className="w-3.5 h-3.5" />
              Static
              <span className="text-xs opacity-70">({staticCount})</span>
            </button>
          </div>
        </div>
        {/* Type filter */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterType('all')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150',
              filterType === 'all'
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
            )}
          >
            All
          </button>
          <button
            onClick={() => setFilterType('shared')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150',
              filterType === 'shared'
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
            )}
          >
            Shared
          </button>
          {appTypes.map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors duration-150',
                filterType === type
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
              )}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Apps Grid */}
      {isLoading && filteredApps.length === 0 ? (
        <div className="text-center py-16 text-khiimori-text-muted">Loading your apps…</div>
      ) : filteredApps.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredApps.map((app) => {
            const cardDescription = getAppCardDescription(app);
            const doc = resolveAppDocument(app);
            const showCardDescription =
              cardDescription && !doc?.components?.some((c) => c.type === 'header');
            return (
            <div
              key={app.id}
              className="group bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl p-5 hover:border-khiimori-border/20 hover:shadow-lifted transition-all duration-200"
            >
              {/* App header */}
              <div className="flex items-start justify-between mb-3 gap-2">
                <Link to={`/apps/${app.id}`} className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-khiimori-text-primary truncate group-hover:text-primary-400 transition-colors">
                    {app.name}
                  </h3>
                  <p className="text-xs text-khiimori-text-muted capitalize flex items-center gap-1.5 flex-wrap">
                    {app.raw_app_type ?? app.app_type}
                    {isDynamicApp(app) ? (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300 normal-case">
                        <Zap className="w-2.5 h-2.5" /> Dynamic
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-300 normal-case">
                        <FileText className="w-2.5 h-2.5" /> Static
                      </span>
                    )}
                    {app.is_shared && (
                      <span className="inline-flex items-center rounded-full bg-primary-500/15 px-1.5 py-0.5 text-[10px] font-medium text-primary-300 normal-case">
                        Shared
                      </span>
                    )}
                  </p>
                </Link>
                <div className="flex items-center shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShareTarget(app);
                    }}
                    aria-label={`Share ${app.name}`}
                    className="p-1.5 rounded text-khiimori-text-muted hover:text-primary-400 hover:bg-primary-500/10 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleToggleBookmark(app.id, e)}
                    aria-label={app.is_bookmarked ? 'Remove bookmark' : 'Bookmark this app'}
                    className={cn(
                      'p-1.5 rounded hover:bg-khiimori-bg-elevated/10 transition-colors focus-visible:ring-2 focus-visible:ring-primary-500/50',
                      app.is_bookmarked ? 'text-warning-400' : 'text-khiimori-text-muted hover:text-khiimori-text-primary'
                    )}
                  >
                    <Bookmark className={`w-4 h-4 ${app.is_bookmarked ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteClick(app, e)}
                    aria-label={`Delete ${app.name}`}
                    className="p-1.5 rounded text-khiimori-text-muted hover:text-error-400 hover:bg-error-500/10 transition-colors focus-visible:ring-2 focus-visible:ring-error-500/50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <Link to={`/apps/${app.id}`} className="block mb-3 min-h-[4.5rem]">
                {showCardDescription ? (
                  <p className="text-xs text-khiimori-text-muted line-clamp-2 mb-2">{cardDescription}</p>
                ) : null}
                <AppCardPreview app={app} />
              </Link>

              {/* App meta */}
              <div className="flex items-center justify-between pt-3 border-t border-khiimori-border/10">
                <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', statusColors[app.status])}>
                  {app.status}
                </span>
                <span className="text-xs text-khiimori-text-muted">
                  {formatRelativeTime(app.created_at)}
                </span>
              </div>

              {/* Tech stack */}
              {app.tech_stack && app.tech_stack !== '—' && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {app.tech_stack.split(',').slice(0, 3).map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-0.5 text-xs bg-khiimori-bg-elevated/10 text-khiimori-text-secondary border border-khiimori-border/20 rounded"
                    >
                      {tech.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-500/10 flex items-center justify-center text-primary-400">
            <Sparkles className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-medium text-khiimori-text-primary mb-1">No apps yet</h3>
          <p className="text-sm text-khiimori-text-muted mb-4">
            Generate your first app from your tabs
          </p>
          <Button leftIcon={<Sparkles className="w-5 h-5" />} onClick={() => setShowCreateModal(true)}>
            Generate App
          </Button>
        </div>
      )}

      <ShareResourceModal
        isOpen={shareTarget != null}
        onClose={() => setShareTarget(null)}
        resourceType="app"
        resourceId={shareTarget?.id ?? ''}
        resourceName={shareTarget?.name ?? 'App'}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={appToDelete != null}
        onClose={() => !isDeleting && setAppToDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
        title="Delete app?"
        message={
          appToDelete
            ? `"${appToDelete.name}" will be permanently removed. This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Create App Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Generate New App"
        description="Create a new app from your browser tabs using AI"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="App Name (optional)"
            placeholder="My Awesome App"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-khiimori-text-secondary mb-1.5">
              App Type
            </label>
            <div className="flex flex-wrap gap-2">
              {appTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAppType(type)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150 capitalize',
                    appType === type
                      ? 'bg-primary-500 text-white'
                      : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
                  )}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <Textarea
            label="Description (optional)"
            placeholder="Describe what kind of app you want to create..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleCreateApp}
              isLoading={isLoading}
            >
              Generate
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}